import type { Meta } from "@/@types/paginated";
import { PayrollVoucherStatusEnum } from "@/enum/payroll-voucher-status";
import {
  MOCK_RECIPIENTS,
  MOCK_RECIPIENT_HISTORY,
} from "@/pages/conciliation/sector-emails/mocks";
import type {
  RecipientHistoryEntry,
  SectorRecipient,
} from "@/pages/conciliation/sector-emails/types";
import {
  MOCK_CYCLE,
  MOCK_VOUCHERS,
} from "@/pages/conciliation/payroll-voucher-approval/mocks";
import type {
  CycleSummary,
  PayrollVoucher,
} from "@/pages/conciliation/payroll-voucher-approval/types";

export interface RecipientPage {
  data: SectorRecipient[];
  meta: Meta;
  total_unfiltered: number;
}

export interface RecipientListQuery {
  search: string;
  sectors: string[];
  shipping_types: string[];
  active: boolean[];
  page: number;
  perPage: number;
}

export type RecipientPayload = Omit<SectorRecipient, "id" | "active">;

export interface VoucherDecisionPayload {
  status: PayrollVoucherStatusEnum;
  version: number;
}

const FIRST_PAGE: number = 1;

let recipients: SectorRecipient[] = MOCK_RECIPIENTS.map(
  (recipient: SectorRecipient): SectorRecipient => ({ ...recipient })
);

let vouchers: PayrollVoucher[] = MOCK_VOUCHERS.map(
  (voucher: PayrollVoucher): PayrollVoucher => ({ ...voucher })
);

const nextRecipientId = (): number =>
  recipients.reduce(
    (highest: number, recipient: SectorRecipient): number =>
      Math.max(highest, recipient.id),
    0
  ) + 1;

const matchesSearch = (recipient: SectorRecipient, search: string): boolean => {
  if (!search) return true;

  const term: string = search.trim().toLowerCase();
  const haystack: string = [
    recipient.sector,
    recipient.name,
    recipient.email,
    ...recipient.shipping_types,
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(term);
};

const matchesFilters = (
  recipient: SectorRecipient,
  query: RecipientListQuery
): boolean =>
  matchesSearch(recipient, query.search) &&
  (query.active.length === 0 || query.active.includes(recipient.active)) &&
  (query.sectors.length === 0 || query.sectors.includes(recipient.sector)) &&
  (query.shipping_types.length === 0 ||
    recipient.shipping_types.some((type: string): boolean =>
      query.shipping_types.includes(type)
    ));

export const listRecipients = (query: RecipientListQuery): RecipientPage => {
  const filtered: SectorRecipient[] = recipients.filter(
    (recipient: SectorRecipient): boolean => matchesFilters(recipient, query)
  );

  const total: number = filtered.length;
  const lastPage: number = Math.max(
    FIRST_PAGE,
    Math.ceil(total / query.perPage)
  );
  const currentPage: number = Math.min(query.page, lastPage);
  const start: number = (currentPage - FIRST_PAGE) * query.perPage;

  return {
    data: filtered.slice(start, start + query.perPage),
    meta: {
      currentPage,
      lastPage,
      perPage: query.perPage,
      total,
      prev: currentPage > FIRST_PAGE ? currentPage - 1 : null,
      next: currentPage < lastPage ? currentPage + 1 : null,
    },
    total_unfiltered: recipients.length,
  };
};

export const createRecipient = (
  payload: RecipientPayload
): SectorRecipient => {
  const created: SectorRecipient = {
    ...payload,
    id: nextRecipientId(),
    active: true,
  };

  recipients = [...recipients, created];
  return created;
};

export const findRecipient = (id: number): SectorRecipient | undefined =>
  recipients.find((recipient: SectorRecipient): boolean => recipient.id === id);

export const updateRecipient = (
  id: number,
  payload: RecipientPayload
): SectorRecipient | undefined => {
  const current: SectorRecipient | undefined = findRecipient(id);
  if (!current) return undefined;

  const updated: SectorRecipient = { ...current, ...payload };

  recipients = recipients.map((recipient: SectorRecipient): SectorRecipient =>
    recipient.id === id ? updated : recipient
  );

  return updated;
};

export const changeRecipientStatus = (
  id: number,
  active: boolean
): SectorRecipient | undefined => {
  const current: SectorRecipient | undefined = findRecipient(id);
  if (!current) return undefined;

  const updated: SectorRecipient = { ...current, active };
  recipients = recipients.map((recipient: SectorRecipient): SectorRecipient =>
    recipient.id === id ? updated : recipient
  );

  return updated;
};

export const removeRecipient = (id: number): boolean => {
  const existed: boolean = !!findRecipient(id);
  recipients = recipients.filter(
    (recipient: SectorRecipient): boolean => recipient.id !== id
  );
  return existed;
};

export const listRecipientHistory = (): readonly RecipientHistoryEntry[] =>
  MOCK_RECIPIENT_HISTORY;

export const listVouchers = (
  status: string | null
): { data: PayrollVoucher[]; meta: Meta } => {
  const filtered: PayrollVoucher[] = status
    ? vouchers.filter(
        (voucher: PayrollVoucher): boolean => voucher.status === status
      )
    : vouchers;

  return {
    data: filtered,
    meta: {
      currentPage: FIRST_PAGE,
      lastPage: FIRST_PAGE,
      perPage: filtered.length,
      total: filtered.length,
      prev: null,
      next: null,
    },
  };
};

export const cycleSummary = (): CycleSummary => ({
  ...MOCK_CYCLE,
  vouchers_in_cycle: vouchers.length,
});

export const findVoucher = (id: number): PayrollVoucher | undefined =>
  vouchers.find((voucher: PayrollVoucher): boolean => voucher.id === id);

export const decideVoucher = (
  id: number,
  payload: VoucherDecisionPayload
): PayrollVoucher | undefined => {
  const current: PayrollVoucher | undefined = findVoucher(id);
  if (!current) return undefined;

  const decided: PayrollVoucher = {
    ...current,
    status: payload.status,
    version: current.version + 1,
  };

  vouchers = vouchers.map((voucher: PayrollVoucher): PayrollVoucher =>
    voucher.id === id ? decided : voucher
  );

  return decided;
};

export const resetRhMockStore = (): void => {
  recipients = MOCK_RECIPIENTS.map(
    (recipient: SectorRecipient): SectorRecipient => ({ ...recipient })
  );
  vouchers = MOCK_VOUCHERS.map(
    (voucher: PayrollVoucher): PayrollVoucher => ({ ...voucher })
  );
};
